import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Database, Brain, Camera, Trash2, RefreshCw, Wifi, AlertTriangle } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../lib/supabase';

// Format relative time ("5 menit yang lalu") — lokal-friendly
const formatRelative = (iso, lang = 'id') => {
  if (!iso) return lang === 'id' ? 'belum ada' : 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return lang === 'id' ? `${sec}d yang lalu` : `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return lang === 'id' ? `${min}m yang lalu` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return lang === 'id' ? `${hr}j yang lalu` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return lang === 'id' ? `${day}h yang lalu` : `${day}d ago`;
};

const StatusDot = ({ status, label }) => {
  const cfg = {
    online:     { color: 'var(--color-green)', pulse: true,  text: 'ONLINE' },
    offline:    { color: '#ff2d55',            pulse: false, text: 'OFFLINE' },
    connecting: { color: '#ffd60a',            pulse: true,  text: 'CONNECTING' },
    waking:     { color: '#ffd60a',            pulse: true,  text: 'WAKING' },
    unknown:    { color: 'var(--text-mute)',   pulse: false, text: '—' },
  }[status] || { color: 'var(--text-mute)', pulse: false, text: '—' };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${cfg.pulse ? 'animate-blink' : ''}`}
        style={{ background: cfg.color }} />
      <span className="text-[10px] font-mono font-black" style={{ color: cfg.color }}>{label || cfg.text}</span>
    </div>
  );
};

const StatusCard = ({ icon: Icon, title, status, value, sub }) => (
  <div className="rounded-xl p-4 flex flex-col gap-2"
    style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={14} color="var(--color-cyan)" />
        <p className="text-[9px] font-black uppercase tracking-widest font-mono"
          style={{ color: 'var(--text-dim)' }}>
          {title}
        </p>
      </div>
      <StatusDot status={status} />
    </div>
    {value && (
      <p className="text-2xl font-black font-mono leading-none" style={{ color: 'var(--text-hi)' }}>
        {value}
      </p>
    )}
    {sub && (
      <p className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>{sub}</p>
    )}
  </div>
);

const SystemLog = ({ supaStatus = 'connecting', hfStatus = 'waking', kameras = [] }) => {
  const { lang } = useApp();
  const [logs, setLogs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('horus_system_logs') || '[]'); } catch { return []; }
  });
  const [filter, setFilter] = useState('all'); // all | error | info | success
  const [counts, setCounts] = useState({ penduduk: 0, kendaraan: 0, alerts: 0, alerts24h: 0 });
  const [lastRealtime, setLastRealtime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Refresh handler — bisa manual atau auto via interval
  const refreshLogs = useCallback(() => {
    try { setLogs(JSON.parse(sessionStorage.getItem('horus_system_logs') || '[]')); } catch {}
  }, []);

  // Auto-refresh logs setiap 2 detik (toggleable)
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(refreshLogs, 2000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshLogs]);

  // Fetch DB stats sekali on mount + setiap 30 detik
  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      const cutoff = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const [pdk, kdr, alr, alr24] = await Promise.all([
        supabase.from('penduduk').select('*', { count: 'exact', head: true }),
        supabase.from('kendaraan').select('*', { count: 'exact', head: true }),
        supabase.from('alerts').select('*', { count: 'exact', head: true }),
        supabase.from('alerts').select('*', { count: 'exact', head: true }).gte('created_at', cutoff),
      ]);
      if (!mounted) return;
      setCounts({
        penduduk:  pdk.count ?? 0,
        kendaraan: kdr.count ?? 0,
        alerts:    alr.count ?? 0,
        alerts24h: alr24.count ?? 0,
      });
    };
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // Subscribe realtime — track last event timestamp
  useEffect(() => {
    const channel = supabase.channel('syslog-monitor')
      .on('postgres_changes', { event: '*', schema: 'public' }, () => {
        setLastRealtime(new Date().toISOString());
      })
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch {} };
  }, []);

  const onlineCameras = kameras.filter(k => k.status === 'ONLINE').length;
  const totalCameras  = kameras.length;

  const filtered = logs.filter(l => {
    if (filter === 'all') return true;
    if (filter === 'error') return l.color === '#ff2d55' || l.color === '#ff6b2b';
    if (filter === 'info')  return l.color === 'var(--color-cyan)' || l.color === 'var(--text-dim)';
    if (filter === 'success') return l.color === 'var(--color-green)';
    return true;
  });

  const clearLogs = () => {
    try { sessionStorage.removeItem('horus_system_logs'); } catch {}
    setLogs([]);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden p-4 gap-4" style={{ background: 'var(--color-bg)' }}>
      {/* Title bar */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Activity size={14} color="var(--color-cyan)" />
          <p className="text-[11px] font-black uppercase tracking-widest font-mono"
            style={{ color: 'var(--color-cyan)' }}>
            {lang === 'id' ? 'Monitor Sistem' : 'System Monitor'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Manual refresh button */}
          <button onClick={refreshLogs}
            className="flex items-center gap-1.5 text-[9px] font-mono font-bold px-2.5 py-1.5 rounded-lg transition-all hover:opacity-80"
            style={{ background: 'rgba(0,212,255,0.10)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}
            title={lang === 'id' ? 'Refresh manual sekarang' : 'Manual refresh now'}>
            <RefreshCw size={11} />
            {lang === 'id' ? 'Refresh' : 'Refresh'}
          </button>

          {/* Auto-refresh toggle */}
          <button onClick={() => setAutoRefresh(v => !v)}
            className="flex items-center gap-1.5 text-[9px] font-mono font-bold px-2.5 py-1.5 rounded-lg transition-all hover:opacity-80"
            style={{
              background: autoRefresh ? 'rgba(0,255,136,0.10)' : 'var(--color-surface)',
              border: `1px solid ${autoRefresh ? 'rgba(0,255,136,0.3)' : 'var(--border-dim)'}`,
              color: autoRefresh ? 'var(--color-green)' : 'var(--text-mute)',
            }}
            title={lang === 'id'
              ? (autoRefresh ? 'Auto-refresh aktif (tiap 2 detik). Klik untuk matikan.' : 'Auto-refresh mati. Klik untuk aktifkan.')
              : (autoRefresh ? 'Auto-refresh on (every 2s). Click to disable.' : 'Auto-refresh off. Click to enable.')}>
            <div className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'animate-blink' : ''}`}
              style={{ background: autoRefresh ? 'var(--color-green)' : 'var(--text-mute)' }} />
            {lang === 'id'
              ? (autoRefresh ? 'AUTO ON' : 'AUTO OFF')
              : (autoRefresh ? 'AUTO ON' : 'AUTO OFF')}
          </button>
        </div>
      </div>

      {/* Status grid — 4 cards: DB, AI, Camera, Realtime */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
        <StatusCard
          icon={Database}
          title={lang === 'id' ? 'Basis Data' : 'Database'}
          status={supaStatus}
          value={`${counts.penduduk + counts.kendaraan}`}
          sub={`${counts.penduduk} ${lang === 'id' ? 'penduduk' : 'people'} · ${counts.kendaraan} ${lang === 'id' ? 'kendaraan' : 'vehicles'}`}
        />
        <StatusCard
          icon={Brain}
          title={lang === 'id' ? 'Mesin AI' : 'AI Engine'}
          status={hfStatus}
          value={hfStatus === 'online' ? 'ON' : hfStatus === 'offline' ? 'OFF' : '...'}
          sub={'KESVision · KESFace · KESOCR'}
        />
        <StatusCard
          icon={Camera}
          title={lang === 'id' ? 'Kamera CCTV' : 'CCTV Cameras'}
          status={onlineCameras > 0 ? 'online' : totalCameras > 0 ? 'offline' : 'unknown'}
          value={`${onlineCameras}/${totalCameras}`}
          sub={lang === 'id'
            ? `${totalCameras - onlineCameras} offline`
            : `${totalCameras - onlineCameras} offline`}
        />
        <StatusCard
          icon={AlertTriangle}
          title={lang === 'id' ? 'Peringatan 24j' : 'Alerts 24h'}
          status={counts.alerts24h > 0 ? 'online' : 'unknown'}
          value={`${counts.alerts24h}`}
          sub={`${counts.alerts} ${lang === 'id' ? 'total semua waktu' : 'total all-time'}`}
        />
      </div>

      {/* Connection metrics row */}
      <div className="rounded-xl p-3 flex flex-wrap items-center gap-x-6 gap-y-1.5 shrink-0"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
        <div className="flex items-center gap-2">
          <Wifi size={11} color="var(--color-green)" />
          <span className="text-[9px] font-mono uppercase tracking-wider"
            style={{ color: 'var(--text-mute)' }}>{lang === 'id' ? 'Realtime terakhir:' : 'Last realtime:'}</span>
          <span className="text-[10px] font-mono font-bold"
            style={{ color: lastRealtime ? 'var(--color-green)' : 'var(--text-mute)' }}>
            {formatRelative(lastRealtime, lang)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono uppercase tracking-wider"
            style={{ color: 'var(--text-mute)' }}>{lang === 'id' ? 'Sesi:' : 'Session:'}</span>
          <span className="text-[10px] font-mono font-bold" style={{ color: 'var(--color-cyan)' }}>
            {sessionStorage.getItem('horus_last_page') || '—'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono uppercase tracking-wider"
            style={{ color: 'var(--text-mute)' }}>{lang === 'id' ? 'Browser:' : 'Browser:'}</span>
          <span className="text-[10px] font-mono font-bold" style={{ color: 'var(--text-dim)' }}>
            {(navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/) || ['—'])[0]}
          </span>
        </div>
      </div>

      {/* Activity Log section */}
      <div className="flex-1 min-h-0 flex flex-col rounded-xl overflow-hidden"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
        <div className="flex items-center justify-between px-4 py-2.5 shrink-0"
          style={{ borderBottom: '1px solid var(--border-lo)' }}>
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-black uppercase tracking-widest font-mono"
              style={{ color: 'var(--text-dim)' }}>
              {lang === 'id' ? 'Log Aktivitas' : 'Activity Log'}
            </p>
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
              {filtered.length}/{logs.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {[
              { id: 'all',     label: lang === 'id' ? 'Semua'   : 'All' },
              { id: 'info',    label: lang === 'id' ? 'Info'    : 'Info' },
              { id: 'success', label: lang === 'id' ? 'Berhasil': 'Success' },
              { id: 'error',   label: lang === 'id' ? 'Error'   : 'Error' },
            ].map(({ id, label }) => (
              <button key={id} onClick={() => setFilter(id)}
                className="text-[8px] font-mono font-bold px-2 py-1 rounded transition-all"
                style={{
                  background: filter === id ? 'rgba(0,212,255,0.15)' : 'transparent',
                  color: filter === id ? 'var(--color-cyan)' : 'var(--text-mute)',
                  border: filter === id ? '1px solid rgba(0,212,255,0.4)' : '1px solid transparent',
                }}>
                {label}
              </button>
            ))}
            {logs.length > 0 && (
              <button onClick={clearLogs}
                className="ml-2 flex items-center gap-1 text-[8px] font-mono font-bold px-2 py-1 rounded hover:opacity-80"
                style={{ background: 'rgba(255,45,85,0.10)', border: '1px solid rgba(255,45,85,0.3)', color: '#ff2d55' }}
                title={lang === 'id' ? 'Hapus semua log' : 'Clear all logs'}>
                <Trash2 size={9} /> {lang === 'id' ? 'Bersihkan' : 'Clear'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto c-scroll p-3 space-y-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 py-12">
              <RefreshCw size={28} color="rgba(0,212,255,0.15)" />
              <p className="text-[10px] font-mono text-center" style={{ color: 'var(--text-mute)' }}>
                {logs.length === 0
                  ? (lang === 'id'
                      ? 'Belum ada log aktivitas. Buka Vision dan aktifkan CCTV/scan untuk generate log.'
                      : 'No activity logs yet. Open Vision and activate CCTV/scan to generate logs.')
                  : (lang === 'id'
                      ? `Tidak ada log dengan filter "${filter}".`
                      : `No logs match filter "${filter}".`)
                }
              </p>
            </div>
          ) : (
            [...filtered].reverse().map((l, i) => (
              <p key={i} className="text-[10px] font-mono leading-relaxed py-0.5 px-2 rounded hover:bg-white/5"
                style={{ color: l.color || 'var(--text-mid)' }}>
                <span style={{ color: 'var(--text-mute)', marginRight: 8 }}>[{l.time}]</span>
                {l.msg}
              </p>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemLog;
